import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Progress,
  Row,
  Col,
  Statistic,
  Badge,
  Input,
  Select,
  Space,
  Button,
  Drawer,
  Descriptions,
  Tooltip,
  Divider,
  Alert,
} from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  BellOutlined,
  ArrowUpOutlined,
  MinusOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import { formatCurrency } from "../../utils/Utils";

const { Option } = Select;

interface ProjectRisk {
  key: string;
  projectName: string;
  plannedTasks: number;
  delayedTasks: number;
  qualityIssues: number;
  dependencyRisks: number;
  totalRiskValue: number;
  costImpact: number;
  status: string;
  riskColor: string;
}

interface TaskRiskInterlinking {
  taskId: string;
  taskName: string;
  delayDays: number;
  projectRisk: string;
  downstreamImpact: string;
  triggeredModule: string;
  monetaryImpact: number;
  automatedMitigation: string;
  responsiblePerson: string;
  status: "Active" | "Mitigated" | "Pending";
}

interface PortfolioMetric {
  key: string;
  metric: string;
  currentValue: string;
  trend: "up" | "down" | "stable";
  trendValue: string;
  riskLevel: "green" | "orange" | "red";
  interpretation: string;
}

const ProjectRiskDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [riskDrawerVisible, setRiskDrawerVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectRisk | null>(
    null
  );
  const [selectedTaskRisks, setSelectedTaskRisks] = useState<
    TaskRiskInterlinking[]
  >([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // Mock data based on the business logic document
  const mockProjectData: ProjectRisk[] = [
    {
      key: "1",
      projectName: "Tower A",
      plannedTasks: 140,
      delayedTasks: 12,
      qualityIssues: 4,
      dependencyRisks: 3,
      totalRiskValue: 3800000,
      costImpact: 8.5,
      status: "High",
      riskColor: "red",
    },
    {
      key: "2",
      projectName: "Villa B",
      plannedTasks: 96,
      delayedTasks: 6,
      qualityIssues: 2,
      dependencyRisks: 1,
      totalRiskValue: 1700000,
      costImpact: 6.3,
      status: "Medium",
      riskColor: "orange",
    },
    {
      key: "3",
      projectName: "Mall Fitout",
      plannedTasks: 180,
      delayedTasks: 8,
      qualityIssues: 5,
      dependencyRisks: 2,
      totalRiskValue: 2200000,
      costImpact: 5.2,
      status: "Medium",
      riskColor: "orange",
    },
    {
      key: "4",
      projectName: "Road Infra",
      plannedTasks: 120,
      delayedTasks: 10,
      qualityIssues: 1,
      dependencyRisks: 2,
      totalRiskValue: 3000000,
      costImpact: 7.1,
      status: "High",
      riskColor: "red",
    },
    {
      key: "5",
      projectName: "Warehouse B",
      plannedTasks: 60,
      delayedTasks: 3,
      qualityIssues: 0,
      dependencyRisks: 0,
      totalRiskValue: 800000,
      costImpact: 2.8,
      status: "Low",
      riskColor: "green",
    },
  ];

  // Mock task risk interlinking data
  const mockTaskRiskData: { [projectKey: string]: TaskRiskInterlinking[] } = {
    "1": [
      {
        taskId: "T-001",
        taskName: "Foundation Work - Block A",
        delayDays: 7,
        projectRisk: "Task Delay (>5 days)",
        downstreamImpact: "Delay in Material Procurement",
        triggeredModule: "Procurement & Work Order",
        monetaryImpact: 520000,
        automatedMitigation:
          "Adjust PR (PR-1024) with TMT Material Need-by date from 2024-03-22  to 2024-03-14 and suggested Suppliers are Shree TMT (On Time)T,Radha TMT (On Time); notify to Desai (procurement head)",
        responsiblePerson: "Amit Sharma (Procurement Manager)",
        status: "Active",
      },
      {
        taskId: "T-002",
        taskName: "Structural Steel Installation",
        delayDays: 9,
        projectRisk: "Dependency Failure",
        downstreamImpact: "Cascading schedule slip",
        triggeredModule: "Project Mgmt / Work Order",
        monetaryImpact: 850000,
        automatedMitigation:
          "Recalculate successor (T-005) start date to 2024-03-25 + lag (3 days)",
        responsiblePerson: "Priya Desai (Project Scheduler)",
        status: "Active",
      },
      {
        taskId: "T-003",
        taskName: "MEP Installation - Phase 1",
        delayDays: 6,
        projectRisk: "Material Shortage",
        downstreamImpact: "Idle site, extended duration",
        triggeredModule: "Inventory / Procurement",
        monetaryImpact: 680000,
        automatedMitigation:
          "Generate PR (PR-1025) for ShortfallQty (350 units) with Need-by date 2024-03-20",
        responsiblePerson: "Suresh Kumar (Store In-charge)",
        status: "Pending",
      },
      {
        taskId: "T-004",
        taskName: "Concrete Pouring - Level 2",
        delayDays: 5,
        projectRisk: "Quality Defect",
        downstreamImpact: "Rework required, schedule delay",
        triggeredModule: "Quality / Work Order",
        monetaryImpact: 450000,
        automatedMitigation:
          "Block RA Bill (RA-2024-018) approval until defect closed by 2024-03-18",
        responsiblePerson: "Rajesh Verma (QA Manager)",
        status: "Active",
      },
      {
        taskId: "T-005",
        taskName: "Plasterwork - Wing A",
        delayDays: 8,
        projectRisk: "Low Resource Utilization",
        downstreamImpact: "Idle manpower cost increasing",
        triggeredModule: "Work Order / HR",
        monetaryImpact: 1300000,
        automatedMitigation:
          "Auto reassign team (Team-05) to delayed task (T-002) from 2024-03-16 + lag (2 days)",
        responsiblePerson: "Vikram Singh (Project Manager)",
        status: "Active",
      },
    ],
    "2": [
      {
        taskId: "T-101",
        taskName: "Excavation Work",
        delayDays: 5,
        projectRisk: "Task Delay (>5 days)",
        downstreamImpact: "Foundation work delayed",
        triggeredModule: "Work Order",
        monetaryImpact: 320000,
        automatedMitigation:
          "Allocate additional resources (Team-03) to task (T-101) starting 2024-03-10 + lag (1 day)",
        responsiblePerson: "Anand Patil (Site Engineer)",
        status: "Active",
      },
      {
        taskId: "T-102",
        taskName: "Plumbing Installation",
        delayDays: 8,
        projectRisk: "Low Resource Utilization",
        downstreamImpact: "Idle manpower cost",
        triggeredModule: "Work Order",
        monetaryImpact: 410000,
        automatedMitigation:
          "Auto reassign team (Team-02) to delayed task (T-104) from 2024-03-14 + lag (2 days)",
        responsiblePerson: "Meera Nair (Project Lead)",
        status: "Pending",
      },
      {
        taskId: "T-103",
        taskName: "Electrical Conduit Installation",
        delayDays: 7,
        projectRisk: "Material Shortage",
        downstreamImpact: "Work stoppage, contractor idle",
        triggeredModule: "Procurement",
        monetaryImpact: 550000,
        automatedMitigation:
          "Generate emergency PR (PR-2012) for ShortfallQty (200 units) with Need-by date 2024-03-12",
        responsiblePerson: "Sanjay Reddy (Procurement Head)",
        status: "Active",
      },
      {
        taskId: "T-104",
        taskName: "Tile Installation - Bathrooms",
        delayDays: 6,
        projectRisk: "Dependency Failure",
        downstreamImpact: "Finishing work delayed",
        triggeredModule: "Project Mgmt",
        monetaryImpact: 420000,
        automatedMitigation:
          "Recalculate successor (T-106) start date to 2024-03-18 + lag (4 days)",
        responsiblePerson: "Kavita Joshi (Project Coordinator)",
        status: "Pending",
      },
    ],
    "3": [
      {
        taskId: "T-201",
        taskName: "Interior Finishing - Floor 1",
        delayDays: 6,
        projectRisk: "Quality Defect",
        downstreamImpact: "Additional rework, delay in closure",
        triggeredModule: "Work Order / Finance",
        monetaryImpact: 550000,
        automatedMitigation:
          "Block RA Bill (RA-2024-025) approval until defect closed by 2024-03-20",
        responsiblePerson: "Deepak Mehta (QA Manager)",
        status: "Active",
      },
      {
        taskId: "T-202",
        taskName: "HVAC Ductwork - Phase 2",
        delayDays: 9,
        projectRisk: "Task Delay (>5 days)",
        downstreamImpact: "MEP completion delayed",
        triggeredModule: "Work Order",
        monetaryImpact: 780000,
        automatedMitigation:
          "Expedite Work Order (WO-3045) by allocating overtime from 2024-03-15 + lag (3 days)",
        responsiblePerson: "Arjun Kulkarni (MEP Manager)",
        status: "Active",
      },
      {
        taskId: "T-203",
        taskName: "False Ceiling - Main Hall",
        delayDays: 5,
        projectRisk: "Material Shortage",
        downstreamImpact: "Interior work stalled",
        triggeredModule: "Procurement / Inventory",
        monetaryImpact: 380000,
        automatedMitigation:
          "Generate PR (PR-3018) for ShortfallQty (120 units) with Need-by date 2024-03-14",
        responsiblePerson: "Neha Gupta (Store Manager)",
        status: "Pending",
      },
      {
        taskId: "T-204",
        taskName: "Marble Flooring - Lobby",
        delayDays: 7,
        projectRisk: "Quality Defect",
        downstreamImpact: "Rework and material wastage",
        triggeredModule: "Quality / Finance",
        monetaryImpact: 490000,
        automatedMitigation:
          "Block RA Bill (RA-2024-028) approval until defect rectified by 2024-03-19",
        responsiblePerson: "Ravi Shankar (QA Lead)",
        status: "Active",
      },
    ],
  };

  const [projectData, setProjectData] =
    useState<ProjectRisk[]>(mockProjectData);
  const [filteredData, setFilteredData] =
    useState<ProjectRisk[]>(mockProjectData);

  // Calculate summary statistics
  const totalProjects = projectData.length;
  const highRiskProjects = projectData.filter(
    (p) => p.status === "High"
  ).length;
  const mediumRiskProjects = projectData.filter(
    (p) => p.status === "Medium"
  ).length;
  const lowRiskProjects = projectData.filter((p) => p.status === "Low").length;
  const totalRiskExposure = projectData.reduce(
    (sum, p) => sum + p.totalRiskValue,
    0
  );
  const totalDelayedTasks = projectData.reduce(
    (sum, p) => sum + p.delayedTasks,
    0
  );
  const avgCostImpact = (
    projectData.reduce((sum, p) => sum + p.costImpact, 0) / totalProjects
  ).toFixed(1);

  // Apply filters
  useEffect(() => {
    let filtered = projectData;

    if (searchText) {
      filtered = filtered.filter((project) =>
        project.projectName.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter.length > 0) {
      filtered = filtered.filter((project) =>
        statusFilter.includes(project.status)
      );
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, projectData]);

  const getRiskColorDot = (color: string) => {
    const colorMap: { [key: string]: string } = {
      red: "#ff4d4f",
      orange: "#faad14",
      green: "#52c41a",
    };
    return (
      <span
        style={{
          display: "inline-block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: colorMap[color] || "#d9d9d9",
          marginRight: 8,
        }}
      />
    );
  };

  const handleDelayedTasksClick = (record: ProjectRisk) => {
    setSelectedProject(record);
    const taskRisks = mockTaskRiskData[record.key] || [];
    setSelectedTaskRisks(taskRisks);
    setExpandedRowKeys([]);
    setRiskDrawerVisible(true);
  };

  const handleRiskInterlinkingClick = (record: ProjectRisk) => {
    handleDelayedTasksClick(record);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "error";
      case "Pending":
        return "warning";
      case "Mitigated":
        return "success";
      default:
        return "default";
    }
  };

  const taskRiskColumns = [
    {
      title: "Task ID",
      dataIndex: "taskId",
      key: "taskId",
      width: 100,
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 200,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Delay Days",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (value: number) => <Tag color="red">{value} days</Tag>,
    },
    {
      title: "Risk Type",
      dataIndex: "projectRisk",
      key: "projectRisk",
      width: 180,
      render: (text: string) => (
        <span>
          <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: "Triggered Module",
      dataIndex: "triggeredModule",
      key: "triggeredModule",
      width: 180,
      render: (text: string) => (
        <Tag color="blue" icon={<LinkOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Monetary Impact",
      dataIndex: "monetaryImpact",
      key: "monetaryImpact",
      width: 150,
      align: "right" as const,
      render: (value: number) => {
        const formattedValue = value ? formatCurrency(value, "INR") : "₹0";
        return (
          <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
            {formattedValue}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center" as const,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
  ];

  const columns = [
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      width: 150,
      fixed: "left" as const,
      render: (text: string, record: ProjectRisk) => (
        <span
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => navigate(`/project-detail-view/${record.key}`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Planned Tasks",
      dataIndex: "plannedTasks",
      key: "plannedTasks",
      width: 120,
      align: "center" as const,
    },
    {
      title: "Delayed Tasks",
      dataIndex: "delayedTasks",
      key: "delayedTasks",
      width: 120,
      align: "center" as const,
      render: (value: number, record: ProjectRisk) => (
        <Tag
          color={value > 10 ? "red" : value > 5 ? "orange" : "green"}
          style={{ cursor: value > 0 ? "pointer" : "default" }}
          onClick={() => value > 0 && handleDelayedTasksClick(record)}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Quality Issues",
      dataIndex: "qualityIssues",
      key: "qualityIssues",
      width: 120,
      align: "center" as const,
      render: (value: number) => (
        <Tag color={value > 3 ? "red" : value > 0 ? "orange" : "green"}>
          {value}
        </Tag>
      ),
    },
    {
      title: "Dependency Risks",
      dataIndex: "dependencyRisks",
      key: "dependencyRisks",
      width: 140,
      align: "center" as const,
      render: (value: number) => (
        <Tag color={value > 2 ? "red" : value > 0 ? "orange" : "green"}>
          {value}
        </Tag>
      ),
    },
    {
      title: "Total Risk Value (₹)",
      dataIndex: "totalRiskValue",
      key: "totalRiskValue",
      width: 160,
      align: "right" as const,
      render: (value: number) => formatCurrency(value, "INR"),
    },
    {
      title: "% Cost Impact",
      dataIndex: "costImpact",
      key: "costImpact",
      width: 140,
      align: "center" as const,
      render: (value: number) => (
        <div>
          <Progress
            percent={value * 10}
            showInfo={false}
            strokeColor={
              value > 7 ? "#ff4d4f" : value > 3 ? "#faad14" : "#52c41a"
            }
          />
          <span style={{ fontWeight: 600 }}>{value}%</span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center" as const,
      render: (status: string) => {
        const colorMap: { [key: string]: string } = {
          High: "error",
          Medium: "warning",
          Low: "success",
        };
        return <Badge status={colorMap[status] as any} text={status} />;
      },
    },
    // {
    //   title: "Risk Color",
    //   dataIndex: "riskColor",
    //   key: "riskColor",
    //   width: 100,
    //   align: "center" as const,
    //   render: (color: string) => getRiskColorDot(color),
    // },
    // {
    //   title: "Action",
    //   key: "action",
    //   width: 150,
    //   align: "center" as const,
    //   fixed: "right" as const,
    //   render: (_: any, record: ProjectRisk) => (
    //     <Button
    //       type="link"
    //       size="small"
    //       icon={<ThunderboltOutlined />}
    //       onClick={() => handleRiskInterlinkingClick(record)}
    //       disabled={record.delayedTasks === 0}
    //     >
    //       Risk Interlinking
    //     </Button>
    //   ),
    // },
  ];

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return <ArrowUpOutlined style={{ color: "#ff4d4f" }} />;
      case "down":
        return <ArrowDownOutlined style={{ color: "#52c41a" }} />;
      case "stable":
        return <MinusOutlined style={{ color: "#1890ff" }} />;
      default:
        return null;
    }
  };

  const getRiskBadge = (riskLevel: string) => {
    const statusMap: { [key: string]: any } = {
      green: "success",
      orange: "warning",
      red: "error",
    };
    return <Badge status={statusMap[riskLevel]} />;
  };

  const portfolioColumns = [
    {
      title: "Metric",
      dataIndex: "metric",
      key: "metric",
      width: 220,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Current Value",
      dataIndex: "currentValue",
      key: "currentValue",
      width: 150,
      align: "center" as const,
      render: (text: string) => (
        <span style={{ fontSize: 16, fontWeight: 600 }}>{text}</span>
      ),
    },
    {
      title: "Trend",
      dataIndex: "trend",
      key: "trend",
      width: 120,
      align: "center" as const,
      render: (trend: string, record: PortfolioMetric) => (
        <Space>
          {getTrendIcon(trend)}
          <span>{record.trendValue}</span>
        </Space>
      ),
    },
    {
      title: "Risk Level",
      dataIndex: "riskLevel",
      key: "riskLevel",
      width: 100,
      align: "center" as const,
      render: (riskLevel: string) => getRiskBadge(riskLevel),
    },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
      width: 280,
      render: (text: string) => (
        <span style={{ color: "#595959" }}>{text}</span>
      ),
    },
  ];
  const mockPortfolioData: PortfolioMetric[] = [
    {
      key: "1",
      metric: "Active Projects",
      currentValue: "18",
      trend: "up",
      trendValue: "+2 MoM",
      riskLevel: "green",
      interpretation: "Normal operational load",
    },
    {
      key: "2",
      metric: "Delayed Tasks",
      currentValue: "42 / 980",
      trend: "up",
      trendValue: "+8",
      riskLevel: "orange",
      interpretation: "Manageable delay zone",
    },
    {
      key: "3",
      metric: "Avg Delay Duration",
      currentValue: "9.5 days",
      trend: "up",
      trendValue: "+1.2 days",
      riskLevel: "orange",
      interpretation: "Schedule tightening needed",
    },
    {
      key: "4",
      metric: "Overall Progress",
      currentValue: "72%",
      trend: "stable",
      trendValue: "Stable",
      riskLevel: "green",
      interpretation: "On track",
    },
    {
      key: "5",
      metric: "Budget Utilized",
      currentValue: "78%",
      trend: "up",
      trendValue: "+3%",
      riskLevel: "orange",
      interpretation: "Near upper threshold",
    },
    {
      key: "6",
      metric: "Total Risk Exposure (₹)",
      currentValue: "₹1.85 Cr",
      trend: "up",
      trendValue: "+0.25 Cr",
      riskLevel: "red",
      interpretation: "Financial oversight required",
    },
    {
      key: "7",
      metric: "Projected Cost Overrun %",
      currentValue: "6.8%",
      trend: "up",
      trendValue: "+1.1%",
      riskLevel: "red",
      interpretation: "Monitor work order allocations",
    },
    {
      key: "8",
      metric: "Cashflow Hold (Pending Payments)",
      currentValue: "₹95L",
      trend: "up",
      trendValue: "+12L",
      riskLevel: "orange",
      interpretation: "Delay in RA approvals",
    },
    {
      key: "9",
      metric: "Material Shortage Incidents",
      currentValue: "15",
      trend: "down",
      trendValue: "-4",
      riskLevel: "green",
      interpretation: "Procurement response improved",
    },
  ];
  const [portfolioData] = useState<PortfolioMetric[]>(mockPortfolioData);
  return (
    <>
      <div className="nk-content-body">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">
                Project Risk Summary Dashboard
              </h3>
              <div className="nk-block-des text-soft">
                <p>
                  Comprehensive view of all project risks and their financial
                  impact
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="nk-block">
          <Card className="card-bordered">
            <div className="card-aside-wrap">
              {/* <div
                className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
                  sm ? "content-active" : ""
                }`}
              >
                <ProjectManagementAside updateSm={setSm} sm={sm} />
              </div> */}
              <div className="card-inner card-inner-lg">
                {/* Summary Statistics */}
                {/* Portfolio Snapshot */}
                <Card
                  title="📊 Portfolio Snapshot"
                  style={{ marginBottom: 24 }}
                >
                  <Table
                    columns={portfolioColumns}
                    dataSource={portfolioData}
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 900 }}
                  />
                </Card>

                {/* Main Table */}
                <Card
                  title="Project Level Risk Summary"
                  style={{ marginBottom: 24 }}
                >
                  <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    scroll={{ x: 1400 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} projects`,
                    }}
                  />
                </Card>
                {/* Control Tower Call-to-Action Summary */}
                <Alert
                  message="🎯 Overall Control Tower Call-to-Action"
                  description={
                    <div style={{ marginTop: 12 }}>
                      <Row gutter={[16, 16]}>
                        <Col span={6}>
                          <Statistic
                            title="High-Risk Projects"
                            value={4}
                            valueStyle={{ color: "#ff4d4f" }}
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic
                            title="Medium-Risk Projects"
                            value={7}
                            valueStyle={{ color: "#faad14" }}
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic
                            title="Aggregate Risk Exposure"
                            value="₹1.85 Cr"
                            valueStyle={{ color: "#ff4d4f" }}
                          />
                        </Col>
                        {/* <Col span={6}>
                          <Statistic
                            title="System Actions Initiated"
                            value={19}
                            valueStyle={{ color: "#1890ff" }}
                            suffix="notifications"
                          />
                        </Col> */}
                      </Row>
                      <Divider style={{ margin: "16px 0" }} />
                      <p style={{ marginBottom: 8 }}>
                        <strong>Top Drivers:</strong> Delays, rework, material
                        dependency, and payment lag
                      </p>
                      <p style={{ marginBottom: 0 }}>
                        <strong>Actions:</strong> 19 auto-notifications, 4
                        escalations, 2 task reschedules
                      </p>
                    </div>
                  }
                  type="warning"
                  icon={<BellOutlined />}
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Risk Interlinking Drawer */}
      <Drawer
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            Task-wise Risk Interlinking - {selectedProject?.projectName}
          </span>
        }
        placement="right"
        width="85%"
        onClose={() => setRiskDrawerVisible(false)}
        open={riskDrawerVisible}
      >
        {selectedProject && (
          <>
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Project">
                {selectedProject.projectName}
              </Descriptions.Item>
              <Descriptions.Item label="Total Delayed Tasks">
                <Tag color="red">{selectedProject.delayedTasks}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Risk Status">
                <Badge
                  status={
                    selectedProject.status === "High"
                      ? "error"
                      : selectedProject.status === "Medium"
                      ? "warning"
                      : "success"
                  }
                  text={selectedProject.status}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Total Risk Value">
                {formatCurrency(
                  selectedTaskRisks.reduce(
                    (sum, task) => sum + task.monetaryImpact,
                    0
                  ),
                  "INR"
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Delayed Tasks with Risk Interlinking</Divider>

            <Table
              columns={taskRiskColumns}
              dataSource={selectedTaskRisks}
              rowKey="taskId"
              pagination={false}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  setExpandedRowKeys(expanded ? [record.taskId] : []);
                },
                expandedRowRender: (record) => (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <p>
                          <strong>Downstream Impact:</strong>
                        </p>
                        <p>{record.downstreamImpact}</p>
                      </Col>
                      <Col span={12}>
                        <p>
                          <strong>Responsible Person:</strong>
                        </p>
                        <Badge color="purple" text={record.responsiblePerson} />
                      </Col>
                      <Col span={24}>
                        <p>
                          <strong>Automated Mitigation:</strong>
                        </p>
                        <p style={{ color: "#52c41a" }}>
                          <ThunderboltOutlined style={{ marginRight: 8 }} />
                          {record.automatedMitigation}
                        </p>
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </>
        )}
      </Drawer>
    </>
  );
};

export default ProjectRiskDashboard;
